"use client";

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

interface SelectOption {
  label: string;
  value: string;
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: readonly SelectOption[];
  className?: string;
  size?: "default" | "sm";
}

export function Select({ value, onChange, options, className, size = "default" }: SelectProps) {
  const listboxId = useId();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const activeIndex = options.findIndex((o) => o.value === value);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const selectedLabel =
    options.find((o) => o.value === value)?.label ?? options[0]?.label ?? "";

  const close = useCallback(() => {
    setOpen(false);
    setFocusedIndex(-1);
  }, []);

  // close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, close]);

  // close on escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, close]);

  // scroll focused item into view
  useEffect(() => {
    if (!open || focusedIndex < 0 || !listRef.current) return;
    const items = listRef.current.children;
    if (items[focusedIndex]) {
      (items[focusedIndex] as HTMLElement).scrollIntoView({ block: "nearest" });
    }
  }, [open, focusedIndex]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setOpen(true);
        setFocusedIndex(activeIndex >= 0 ? activeIndex : 0);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev < options.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (focusedIndex >= 0) {
          onChange(options[focusedIndex].value);
        }
        close();
        break;
      case "Tab":
        close();
        break;
    }
  };

  return (
    <div ref={ref} className={`relative ${className ?? ""}`}>
      <button
        type="button"
        role="combobox"
        aria-controls={listboxId}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => {
          if (open) {
            close();
          } else {
            setOpen(true);
            setFocusedIndex(activeIndex >= 0 ? activeIndex : 0);
          }
        }}
        onKeyDown={handleKeyDown}
        className={`flex w-full cursor-pointer items-center gap-2 border font-mono text-neutral-300 outline-none transition-colors hover:border-neutral-700 focus:border-acid/50 ${
          size === "sm"
            ? "rounded border-neutral-700 bg-dark py-1 pl-2 pr-1.5 text-[11px]"
            : "rounded-lg border-neutral-800 bg-surface py-1.5 pl-3 pr-2.5 text-xs"
        }`}
      >
        <span className="truncate">{selectedLabel}</span>
        <ChevronDown
          size={size === "sm" ? 12 : 14}
          strokeWidth={2}
          className={`shrink-0 text-neutral-500 transition-transform duration-150 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          className={`absolute left-0 top-full z-50 mt-1 max-h-60 min-w-full overflow-auto border bg-surface shadow-xl shadow-black/50 ${
            size === "sm"
              ? "rounded border-neutral-700 bg-dark py-0.5"
              : "rounded-lg border-neutral-800 py-1"
          }`}
        >
          {options.map((option, i) => {
            const isActive = option.value === value;
            const isFocused = i === focusedIndex;
            return (
              <li
                key={option.value}
                role="option"
                aria-selected={isActive}
                onMouseEnter={() => setFocusedIndex(i)}
                onClick={() => {
                  onChange(option.value);
                  close();
                }}
                className={`cursor-pointer font-mono transition-colors ${
                  size === "sm" ? "px-2 py-1 text-[11px]" : "px-3 py-1.5 text-xs"
                } ${
                  isActive
                    ? "text-acid"
                    : isFocused
                      ? "bg-neutral-800/80 text-white"
                      : "text-neutral-400 hover:bg-neutral-800/50 hover:text-neutral-200"
                }`}
              >
                {option.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
